/**
 * The Access tab — `organization-build-book.md` §6.10, `org-10-org-access.png`.
 *
 * Who can see this place, and why. The "why" is the part that is hard to get
 * at anywhere else: a grant made three levels up reaches down here without
 * appearing on anybody's list of this branch's people, which is how a company
 * discovers after the fact that the whole region could read York's margins.
 *
 * So there are three groups rather than one list — granted here, inherited
 * from above, and granted in a branch below — and the middle one links to the
 * place the grant was actually made rather than pretending it belongs here.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { GrantView, MeResponse, OrgAccess, OrgUnitDetail } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { request } from '../../lib/api.js'
import { formatDocumentDate } from '../../lib/format.js'
import { Avatar, Band, Button, Tag } from '../primitives.js'
import { ConfirmDialog, Popover } from '../overlays.js'
import {
  RECORD_ICONS, IconLock, IconMore, IconPlus, IconShieldCheck,
} from '../../frame/icons.js'
import { GrantAccessDialog } from './dialogs.js'

export function OrgAccessTab({ me, unit, canEdit }: {
  me: MeResponse; unit: OrgUnitDetail; canEdit: boolean
}) {
  const [access, setAccess] = useState<OrgAccess | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [granting, setGranting] = useState(false)
  const [removing, setRemoving] = useState<GrantView | null>(null)
  /* Changing a role is granting a new one: the dialog opens knowing who. */
  const [changing, setChanging] = useState<GrantView | null>(null)

  const load = useCallback(async () => {
    try {
      setAccess(await request<OrgAccess>(`/org-units/${unit.id}/access`))
      setError(null)
    } catch (problem) {
      setError((problem as { message?: string }).message ?? 'That did not load.')
    }
  }, [unit.id])

  useEffect(() => { void load() }, [load])

  async function remove(grant: GrantView) {
    try {
      await request(`/people/${grant.userId}/grants/${grant.id}`, { method: 'DELETE' })
      setRemoving(null)
      await load()
    } catch (problem) {
      setError((problem as { message?: string }).message ?? 'That grant stays.')
      setRemoving(null)
    }
  }

  const Kind = RECORD_ICONS[unit.kind]

  return (
    <div className="flex flex-col pb-1">
      <div className="flex items-start gap-2.5 px-[18px] py-3">
        <Band tone="teal" icon={<Kind size={15} />} className="min-w-0 flex-1">
          Access granted <b className="font-bold">here</b> reaches{' '}
          <b className="font-bold">{unit.name}</b>
          {unit.counts.branches > 1
            ? ` and the ${unit.counts.branches - 1} other branch${
              unit.counts.branches === 2 ? '' : 'es'} under it`
            : ''}
          , their yards and their crews. Grants made on a branch stay in that branch.
        </Band>
        {canEdit && (
          <Button variant="primary" className="h-[34px] shrink-0" onClick={() => setGranting(true)}>
            <IconPlus size={15} />Grant access
          </Button>
        )}
      </div>

      {error && <p className="m-0 px-[18px] pb-2 text-[12.5px] text-danger">{error}</p>}

      {access && (
        <>
          <Group
            title="Granted here"
            sub={people(access.here)}
            rows={access.here}
            onRemove={canEdit ? setRemoving : undefined}
            onChangeRole={canEdit ? setChanging : undefined}
          />
          <Group
            title="Inherited from above"
            sub="reach here because of a grant on a parent"
            rows={access.inherited}
          />
          {unit.kind !== 'branch' && (
            <Group
              title="Granted in a branch below"
              sub="only reach that branch"
              rows={access.below}
            />
          )}
        </>
      )}

      <div className={cn('flex items-start gap-2 border-t border-line-faint px-[18px] py-3',
        'text-[12px] leading-[1.45] text-text-muted')}>
        <span className="flex shrink-0 pt-px text-text-faint"><IconShieldCheck size={14} /></span>
        <span>
          A person&apos;s role at a place is the strongest of every grant at that place or
          above it. Roles and their permissions are edited under{' '}
          <b className="font-semibold">People → Users &amp; roles</b>.
        </span>
      </div>

      {(granting || changing) && (
        <GrantAccessDialog
          me={me}
          atUnitId={unit.id}
          person={changing ? { id: changing.userId, name: changing.userName } : undefined}
          onClose={() => { setGranting(false); setChanging(null) }}
          onGranted={() => { setGranting(false); setChanging(null); void load() }}
        />
      )}

      {removing && (
        <ConfirmDialog
          title={`Remove ${removing.userName}'s access?`}
          confirmLabel="Remove access"
          onCancel={() => setRemoving(null)}
          onConfirm={() => void remove(removing)}
          body={(
            <>
              {removing.userName} stops being {removing.roleName} at {removing.orgUnitName}. Their
              work, their hours and their history stay exactly as they are, and the change is
              written to the audit log.
            </>
          )}
        />
      )}
    </div>
  )
}

const people = (rows: GrantView[]): string => {
  const n = new Set(rows.map((row) => row.userId)).size
  return `${n} ${n === 1 ? 'person' : 'people'}`
}

function Group({ title, sub, rows, onRemove, onChangeRole }: {
  title: string
  sub: string
  rows: GrantView[]
  onRemove?: (grant: GrantView) => void
  onChangeRole?: (grant: GrantView) => void
}) {
  return (
    <>
      <div className="flex items-baseline gap-2 px-[18px] pb-1.5 pt-3">
        <b className="text-[13px] text-text">{title}</b>
        <span className="text-[12px] text-text-muted">{sub}</span>
      </div>

      {rows.length === 0
        ? <p className="m-0 px-[18px] pb-2 text-[12.5px] text-text-faint">Nobody.</p>
        : (
          <>
            <div className={cn(GRID, 'border-y border-line-faint bg-surface-alt px-[18px] py-2',
              'text-[10.5px] font-semibold uppercase tracking-[0.06em] text-text-faint',
              // A column header over a stacked row names nothing.
              'max-md:hidden')}>
              <span>Person</span>
              <span>Role</span>
              <span>Granted at</span>
              <span>Reaches</span>
              <span>Since</span>
              <span />
            </div>

            {rows.map((grant) => (
              <div
                key={grant.id}
                className={cn(GRID, 'items-center border-b border-line-faint px-[18px] py-2.5',
                  'last:border-b-0', grant.inherited && 'opacity-[0.72]',
                  /*
                   * Six columns do not fit in 390px. On a phone the row is a
                   * person, their role and where it came from, wrapped — the
                   * reach counts and the date are what a narrow screen can
                   * afford to lose.
                   */
                  'max-md:flex max-md:flex-wrap max-md:gap-x-2')}
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  <Avatar name={grant.userName} size={30} />
                  <span className="flex min-w-0 flex-col">
                    <b className="truncate text-[13.5px] text-text">{grant.userName}</b>
                    {grant.note && (
                      <span className="truncate text-[11.5px] text-text-muted">{grant.note}</span>
                    )}
                  </span>
                </span>

                <span className="min-w-0">
                  <Tag tone={grant.outranked ? 'plain' : 'ok'}>{grant.roleName}</Tag>
                </span>

                <span className={cn('flex min-w-0 items-center gap-1.5 text-[12.5px]',
                  grant.inherited ? 'text-text-faint' : 'text-text-body')}>
                  {grant.inherited && <IconLock size={13} />}
                  <span className="truncate">{grant.orgUnitName}</span>
                </span>

                <span className="truncate text-[12.5px] text-text-muted max-md:hidden">
                  {reaches(grant)}
                </span>

                <span className="text-[12px] text-text-faint max-md:hidden">
                  {formatDocumentDate(grant.createdAt)}
                </span>

                <span className="flex justify-end">
                  {onRemove && !grant.inherited && (
                    <RowMenu
                      grant={grant}
                      onRemove={onRemove}
                      onChangeRole={onChangeRole}
                    />
                  )}
                </span>
              </div>
            ))}
          </>
        )}
    </>
  )
}

/*
 * Narrower than the mockup's, because the mockup draws this card at 940px and
 * the real frame has an app rail and a settings nav in front of it. The five
 * fixed columns came to 530px of a 790px card and every name was drawn as one
 * letter and an ellipsis — the one column that has to be readable.
 */
const GRID = 'grid grid-cols-[minmax(0,1.6fr)_112px_132px_112px_76px_30px] gap-2.5'

/** Change role · Remove — the mockup's ⋯ on a row granted here. */
function RowMenu({ grant, onRemove, onChangeRole }: {
  grant: GrantView
  onRemove: (grant: GrantView) => void
  onChangeRole?: (grant: GrantView) => void
}) {
  const [open, setOpen] = useState(false)
  const at = useRef<HTMLSpanElement>(null)

  return (
    <>
      <span ref={at} className="inline-flex">
        <Button
          variant="ghost"
          aria-label={`More for ${grant.userName}`}
          className="h-[30px] w-[30px] p-0"
          onClick={() => setOpen((was) => !was)}
        >
          <IconMore size={15} />
        </Button>
      </span>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchor={at.current}
        align="right"
        width={186}
        label={`More for ${grant.userName}`}
      >
        <div className="flex flex-col py-1">
          {onChangeRole && (
            <button
              type="button"
              onClick={() => { setOpen(false); onChangeRole(grant) }}
              className="flex h-9 items-center px-3 text-left text-cell text-text hover:bg-hover"
            >
              Change role
            </button>
          )}
          <button
            type="button"
            onClick={() => { setOpen(false); onRemove(grant) }}
            className="flex h-9 items-center px-3 text-left text-cell text-danger hover:bg-danger-bg"
          >
            Remove access
          </button>
        </div>
      </Popover>
    </>
  )
}

/**
 * Two facts, not four. The column is 112px and "1 branch · 4 crews · 63
 * properties" is drawn as "1 branch · 4 crews · 6…", which reads as a number
 * that has been cut in half rather than as a list that carries on.
 */
function reaches(grant: GrantView): string {
  const parts: string[] = []
  if (grant.reaches.branches > 0) {
    parts.push(`${grant.reaches.branches} branch${grant.reaches.branches === 1 ? '' : 'es'}`)
  }
  if (grant.reaches.crews > 0) parts.push(`${grant.reaches.crews} crews`)
  else if (grant.reaches.properties > 0) parts.push(`${grant.reaches.properties} properties`)
  return parts.join(' · ') || 'this place'
}
